import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { IParameterPayload } from 'components/ParameterFormSidebar/types';
import { normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType } from 'utils/types';

import {
  normalizeCreatedTerminal,
  normalizePspTerminalParameters,
  normalizeTerminals,
} from './normalizers';
import {
  IPspTerminal,
  IPspTerminalParameter,
  ITerminalData,
  ITerminalForm,
  IUpdateTerminalPayload,
} from './types';

const terminalsApi = {
  async getAllTerminals(filters: IAllPageFilters): Promise<IPageType<IPspTerminal>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('banks/terminals', {
      params: normalizedFilter,
    });

    return normalizePageData<ITerminalData, IPspTerminal>(
      response.data,
      normalizeTerminals,
      JSON.stringify(filters.search),
    );
  },

  async getTerminal(id: string): Promise<IPspTerminal> {
    const response = await axiosInstance.get(`banks/terminals/${id}`);

    return normalizeTerminals(response.data);
  },

  async getPspTerminalParameters(id: string): Promise<IPspTerminalParameter[]> {
    const response = await axiosInstance.get(`banks/terminals/${id}/parameters`);

    return normalizePspTerminalParameters(response.data);
  },

  async createTerminal(payload: ITerminalForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.post('banks/terminals', normalizedPayload);

    return normalizeCreatedTerminal(response.data);
  },

  async updateTerminal({ id, ...payload }: IUpdateTerminalPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.put(`banks/terminals/${id}`, normalizedPayload);

    return normalizeCreatedTerminal(response.data);
  },

  async createParameter({ id, payload }: IParameterPayload): Promise<AxiosResponse> {
    const normalizedTParameter = prepareFormData(payload);

    const result = await axiosInstance.put(
      `banks/terminals/${id}/parameters`,
      normalizedTParameter,
    );
    return result.data;
  },

  async resetTerminalParameter(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`banks/terminals/parameters/${id}`);

    return response;
  },

  async deleteTerminal(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`/banks/terminals/${id}`);

    return response.data;
  },
};

export default terminalsApi;
